import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { PromisePool } from "@supercharge/promise-pool";
import { useDropzone } from "react-dropzone";
import { useInternet } from "../../../../hooks/useInternet.js";
import { Button } from "../../../../components/button.jsx";
import { format, parseISO } from "date-fns";
import { useResource } from "../../../../hooks/useResource.js";
import { Link, useNavigate } from "react-router-dom";
import { LookupModal } from "./lookupModal.jsx";
import { contactLabel } from "../../../../services/labels.jsx";

export function DocumentsTab({ watch, setValue }) {
  let session = useSelector((state) => state.session);

  let internet = useInternet();

  let documentsService = useResource("documents");
  let [uploadProgress, setUploadProgress] = useState(null);
  let [seeLookupModal, setSeeLookupModal] = useState(false);
  let navigate = useNavigate();

  const onDrop = useCallback(async (files) => {
    setUploadProgress({ i: 0, n: files.length });
    await PromisePool.withConcurrency(10)
      .for(files)
      .onTaskFinished((item, pool) => {
        setUploadProgress({
          i: pool.processedCount(),
          n: files.length,
        });
      })
      .process(async (file) => {
        let data = new FormData();
        data.append("file", file);
        let res = await internet.post("/documents", data);
        setValue("documents", [...watch("documents"), await res.json()], {
          shouldDirty: true,
        });
      });
    await setUploadProgress(null);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  if (watch("noDocumentsRequired")) {
    return (
      <div className={"bg-white p-3 flex flex-col gap-3"}>
        <div>This transaction doesn't require any document</div>
        <div>
          <Button
            sizing={"s"}
            onClick={() =>
              setValue("noDocumentsRequired", false, {
                shouldDirty: true,
              })
            }
          >
            Mark documents as required
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={"flex flex-col gap-3 bg-white bg-white p-3"}>
      {seeLookupModal && (
        <LookupModal
          transaction={watch()}
          onClose={() => setSeeLookupModal(false)}
          onAdd={(doc) => {
            setValue("documents", [...watch("documents"), doc], {
              shouldDirty: true,
            });
          }}
        />
      )}
      <div className={"flex gap-3"}>
        <div
          {...getRootProps()}
          className={
            "border text-center border-gray-400 p-3 rounded border-dashed bg-white hover:bg-blue-50 cursor-pointer flex-grow"
          }
        >
          <input {...getInputProps()} />
          {uploadProgress ? (
            <p>
              {uploadProgress.i} /{uploadProgress.n}
            </p>
          ) : (
            <p>Drop a document</p>
          )}
        </div>
      </div>
      {(watch("documents") || []).map((document) => {
        let { reference, amount, currency, date, contact } = document;
        return (
          <Link
            className={
              "p-2 bg-blue-50 hover:bg-blue-100 cursor-pointer rounded flex gap-3 px-3 items-center"
            }
            to={"/documents/" + document.id}
          >
            <div style={{ width: 150 }} className={"font-semibold"}>
              {date
                ? format(parseISO(date), session.account.dateFormat)
                : "No date"}
            </div>

            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: 200,
              }}
            >
              {reference}
            </div>
            <div>{contactLabel(contact)}</div>
            <div className={"flex-grow"}></div>
            <div>
              <Button
                onClick={(e) => {
                  setValue(
                    "documents",
                    watch("documents").filter((doc) => doc.id != document.id),
                    { shouldDirty: true }
                  );
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <span className={"fa fa-times"} />{" "}
              </Button>
            </div>
          </Link>
        );
      })}
      <Button onClick={() => setSeeLookupModal(true)}>Link a document</Button>

      <Button
        onClick={() =>
          setValue("noDocumentsRequired", true, {
            shouldDirty: true,
          })
        }
      >
        Mark no documents required
      </Button>
    </div>
  );
}
